import { formatDistance } from 'date-fns'
import type {
  ReviewCommentKind,
  ReviewCommentSource,
  ReviewCommentStatus,
  ReviewStatus,
} from '@/db/schema/enums'
import type { CardReviewSummaryDto } from '@/lib/coaching/card-review-query'
import { isClientVisibleCommentStatus } from '@/lib/coaching/review-comment-visibility'
import { isTickedCard } from '@/lib/coaching/submission-kind'
import type { CardReviewSummaryResult } from '../_state/contracts'

/**
 * How the card-details modal reads a review (LG-360 §3).
 *
 * The wire shape carries ISO strings where the DTO has `Date`s (see
 * `CardReviewResult`), and the labels below are the modal's whole vocabulary
 * for a review it can only read. Both live here rather than in the section
 * component so they can be tested without rendering a dialog — the same
 * split `_lib/history-log.ts` makes for §5.
 */

/** Reconciles the wire shape back to the DTO the section is typed against. */
export function parseCardReview(
  review: CardReviewSummaryResult,
): CardReviewSummaryDto {
  return {
    ...review,
    submittedAt: new Date(review.submittedAt),
    reviewedAt: review.reviewedAt === null ? null : new Date(review.reviewedAt),
    comments: review.comments.map((comment) => ({
      ...comment,
      createdAt: new Date(comment.createdAt),
    })),
  }
}

/**
 * Total `Record`s over the schema's own unions, not `Partial<Record<string,
 * …>>`: these cover every member of their enum, so a member added to one
 * has to be given words here before this compiles. Keyed on `string` they
 * would instead fall through to the raw token at runtime — a coach reading
 * "in_review" where a sentence belongs, with nothing to catch it.
 *
 * The `??` fallbacks below are unreachable by type and kept for one case
 * the type cannot cover: a browser tab left open across a deploy that adds
 * an enum member, where the raw token still beats a blank line.
 */
const REVIEW_STATUS_LABEL: Record<ReviewStatus, string> = {
  awaiting: 'Waiting for review',
  in_review: 'In review',
  reviewed: 'Reviewed',
  reopened: 'Reopened',
  superseded: 'Superseded — no longer awaiting a verdict',
}

/**
 * Every `ReviewStatus`, in the words a coach would use for it.
 *
 * No per-kind override for a ticked card (LG-905), because the cause-free
 * wording LG-847 gave `superseded` is already true of one: an un-tick is a
 * third writer of that status beside a cancelled card and a resubmission, and
 * a manual review reaches it through cancellation too — so naming any one
 * cause would be false some of the time here exactly as it is there.
 */
export function reviewStatusLabel(status: ReviewStatus): string {
  return REVIEW_STATUS_LABEL[status] ?? status
}

const COMMENT_STATUS_LABEL: Record<ReviewCommentStatus, string> = {
  pending: 'Draft',
  approved: 'Approved',
  posted: 'Posted',
  dismissed: 'Dismissed',
}

export function commentStatusLabel(status: ReviewCommentStatus): string {
  return COMMENT_STATUS_LABEL[status] ?? status
}

const COMMENT_KIND_LABEL: Record<ReviewCommentKind, string> = {
  praise: 'Praise',
  critique: 'Critique',
  suggestion: 'Suggestion',
}

export function commentKindLabel(kind: ReviewCommentKind): string {
  return COMMENT_KIND_LABEL[kind] ?? kind
}

const COMMENT_SOURCE_LABEL: Record<ReviewCommentSource, string> = {
  ai: 'AI',
  coach: 'Coach',
}

export function commentSourceLabel(source: ReviewCommentSource): string {
  return COMMENT_SOURCE_LABEL[source] ?? source
}

/**
 * The one line under the status: which submission this review is of, and
 * when it landed. `submissionNumber` is stated only past the first, because
 * "submission 1" on a card with one submission is noise; a second one is the
 * whole reason the number exists.
 *
 * A ticked card states neither (LG-905): nothing was submitted, and its
 * `submissionNumber` counts un-ticks rather than attempts, so "Submission 3"
 * on a chapter reading says something untrue about the client. The same rule
 * the Review tab's own chips and the client's feedback list apply.
 */
export function describeSubmission(
  review: CardReviewSummaryDto,
  now: Date,
): string {
  const when = formatDistance(review.submittedAt, now, { addSuffix: true })
  if (isTickedCard(review.kind)) return `Marked done ${when}`
  return review.submissionNumber > 1
    ? `Submission ${review.submissionNumber}, submitted ${when}`
    : `Submitted ${when}`
}

/**
 * When the review was last finished, or null while it never has been.
 *
 * "Last reviewed", not "Reviewed": a `reopened` review — and an `in_review`
 * one that was reopened and re-claimed — still carries the timestamp of the
 * finish that was reopened (`CoachingReview_lifecycle_check` requires it),
 * so the present tense would claim a state the status line beside it
 * contradicts.
 */
export function describeLastReviewed(
  review: CardReviewSummaryDto,
  now: Date,
): string | null {
  return review.reviewedAt === null
    ? null
    : `Last reviewed ${formatDistance(review.reviewedAt, now, { addSuffix: true })}`
}

/**
 * How many comments the client can actually see, out of how many exist.
 *
 * The rule itself is imported, not copied: `review-comment-visibility.ts`
 * is the one definition, shared with `clientVisibleComments` in the review
 * service. A count on a coach's screen claiming what a client can see must
 * not be able to drift from what the client is actually served.
 */
export function countVisibleComments(
  comments: readonly { status: ReviewCommentStatus }[],
): number {
  return comments.filter((comment) =>
    isClientVisibleCommentStatus(comment.status),
  ).length
}
